/**
 * Specific-gift "Transfer to Beneficiary" defaults.
 *
 * When a lawyer sets a share holding's instruction to "Transfer to Beneficiary"
 * in the inventory drawer, the beneficiaries entitled to that holding (via the
 * matter's specific-gift entitlements) should be pre-selected with their unit
 * allocation. This module computes that default selection from the entitlement
 * data the client already fetches (`useEntitlements` + `useEntitlementClasses`).
 *
 * It is a faithful port of the backend canonical resolver
 * (`src/matters/entitlements/utils/specific-gift-beneficiaries.util.ts`), so the
 * inventory drawer's seed matches the redemption-stage seed (LF-3930) exactly.
 * Both surfaces MUST agree on who is entitled to a gifted asset and in what
 * share — keep this file in sync with that backend util.
 */

import type {
  EntitlementBeneficiaryResponse,
  EntitlementClassResponse,
  EntitlementResponse
} from './types';

/** A resolved, real (non-placeholder) beneficiary entitled to a gifted item. */
export interface ItemBeneficiaryShare {
  contactId: number;
  sharePercent: number;
}

/**
 * Effective share (as a percentage) of a single entitlement beneficiary row,
 * taken from its explicit `percentage` or its fraction pair. Returns 0 when
 * neither is set.
 */
export function computeEffectivePercentage(
  b: EntitlementBeneficiaryResponse
): number {
  if (b.percentage) return parseFloat(b.percentage);
  if (b.fractionNumerator && b.fractionDenominator) {
    return (b.fractionNumerator / b.fractionDenominator) * 100;
  }
  return 0;
}

/**
 * Resolve one specific-gift beneficiary row into its effective beneficiaries:
 *  - **named row** → one entry for the named contact;
 *  - **resolved class** → one entry per member, the row's share divided equally
 *    across members (`equal_split`; `per_member` is rejected at validate-time on
 *    share-based masters);
 *  - **unresolved class** → no entries (no contact to attribute yet);
 *  - **fixed-amount row** → no entries. This map seeds a UNIT allocation for an
 *    in-specie transfer, and a dollar sum cannot be turned into units without a
 *    per-unit price the entitlement does not carry (VALE-738). Contributing a
 *    0% share would be worse than contributing nothing: the allocator falls
 *    back to an equal split when the shares carry no weight, so a $50,000 gift
 *    would silently seed an equal division of the whole parcel. Mirrors the
 *    backend's `resolveSpecificGiftBeneficiariesByItem`.
 */
function resolveRow(
  b: EntitlementBeneficiaryResponse,
  classesById: Map<number, EntitlementClassResponse>
): ItemBeneficiaryShare[] {
  if (b.amount != null && b.amount.trim() !== '') return [];
  const sharePercent = computeEffectivePercentage(b);

  if (b.entitlementClassId != null) {
    const klass = classesById.get(b.entitlementClassId);
    const members = klass?.members ?? [];
    const isResolved = klass?.resolvedAt != null && members.length > 0;
    if (!isResolved) return [];
    const memberShare = sharePercent / members.length;
    return members.map((m) => ({
      contactId: m.contactId,
      sharePercent: memberShare
    }));
  }

  if (b.beneficiaryContactId == null) return [];
  return [{ contactId: b.beneficiaryContactId, sharePercent }];
}

/**
 * Map every inventory item that is the subject of a specific-gift entitlement to
 * the resolved beneficiaries entitled to it and their effective share. Shares for
 * the same contact across multiple gift masters covering the same item are
 * summed. Unresolved-class placeholders are omitted — there is no contact to
 * select. Items with no resolvable beneficiary do not appear in the map.
 */
export function resolveSpecificGiftBeneficiariesByItem(
  entitlements: EntitlementResponse[],
  classesById: Map<number, EntitlementClassResponse>
): Map<number, ItemBeneficiaryShare[]> {
  const sharesByItem = new Map<number, Map<number, number>>();

  for (const entitlement of entitlements) {
    if (entitlement.type !== 'specific_gift') continue;
    const itemIds = (entitlement.items ?? []).map(
      (link) => link.inventoryItemId
    );
    if (itemIds.length === 0) continue;

    for (const beneficiary of entitlement.beneficiaries ?? []) {
      for (const entry of resolveRow(beneficiary, classesById)) {
        for (const itemId of itemIds) {
          const perContact =
            sharesByItem.get(itemId) ?? new Map<number, number>();
          perContact.set(
            entry.contactId,
            (perContact.get(entry.contactId) ?? 0) + entry.sharePercent
          );
          sharesByItem.set(itemId, perContact);
        }
      }
    }
  }

  const result = new Map<number, ItemBeneficiaryShare[]>();
  Array.from(sharesByItem.entries()).forEach(([itemId, perContact]) => {
    result.set(
      itemId,
      Array.from(perContact.entries()).map(([contactId, sharePercent]) => ({
        contactId,
        sharePercent
      }))
    );
  });
  return result;
}

/**
 * Allocate `totalUnits` across beneficiaries proportional to their entitlement
 * share, using largest-remainder rounding so the integer parts sum exactly to
 * `totalUnits` (e.g. 755 across equal thirds → 252/252/251). Ties on the
 * fractional part keep input order, so the result is deterministic. When
 * `totalUnits` is unknown or non-positive, every beneficiary is allocated zero
 * units (the caller still seeds the selection; the lawyer allocates later).
 */
export function allocateUnitsByShare(
  totalUnits: number | null | undefined,
  shares: ItemBeneficiaryShare[]
): { contactId: number; units: number }[] {
  if (shares.length === 0) return [];
  if (totalUnits == null || totalUnits <= 0) {
    return shares.map((s) => ({ contactId: s.contactId, units: 0 }));
  }

  const shareTotal = shares.reduce((sum, s) => sum + s.sharePercent, 0);
  // Equal split when the shares carry no weight (defensive — gift rows normally
  // carry a percentage/fraction).
  const weights =
    shareTotal > 0 ? shares.map((s) => s.sharePercent) : shares.map(() => 1);
  const weightTotal = shareTotal > 0 ? shareTotal : shares.length;

  const exact = weights.map((w) => (totalUnits * w) / weightTotal);
  const allocated = exact.map((x) => Math.floor(x));
  let remainder = totalUnits - allocated.reduce((sum, f) => sum + f, 0);

  const byFraction = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac);
  for (const { i } of byFraction) {
    if (remainder <= 0) break;
    allocated[i] += 1;
    remainder -= 1;
  }

  return shares.map((s, i) => ({
    contactId: s.contactId,
    units: allocated[i]
  }));
}
